import { makeAutoObservable } from "mobx";
import FirebaseService from "./FirebaseService";
import { Plan } from "./PlansService";
import {
    addDoc,
    collection,
    getDocs,
    limit,
    orderBy,
    query,
    Timestamp,
    where,
} from "firebase/firestore";
import dayjs from "dayjs";

export type SurveyFeedback = {
    userId: string;
    planId: string;
    planDate: Date;
    submittedAt: Date;
    step1Answer: string;
    mostHelpful?: string | null;
    missingOrOff?: string | null;
    improvementArea?: string | null;
    upgradeBlocker?: string | null;
    otherFeedback?: string | null;
    missingFeaturesText?: string | null;
    userType: "free" | "paid";
};

export default class SurveyService {
    private _planTriggeringSurvey: Plan | null = null;
    private _shouldShowSurvey = false;
    private _lastCheckedUserId: string | null = null;

    get planTriggeringSurvey() {
        return this._planTriggeringSurvey;
    }

    get shouldShowSurvey() {
        return this._shouldShowSurvey;
    }

    constructor(private _firebaseService: FirebaseService) {
        makeAutoObservable(this, undefined, { autoBind: true, deep: false });
    }

    async checkFromPlans(userId: string, plans: Plan[]): Promise<void> {
        if (this._lastCheckedUserId === userId) return;

        try {
            const db = this._firebaseService.db;

            // Load all plan check-ins for this user (there won't be many — max ~4/year)
            const q = query(
                collection(db, "surveys"),
                where("userId", "==", userId),
                where("type", "==", "planCheckIn"),
                orderBy("submittedAt", "desc"),
                limit(50)
            );
            const snapshot = await getDocs(q);
            const pastSurveys = snapshot.docs.map((d) => d.data());

            // Mark checked only after a successful query
            this._lastCheckedUserId = userId;

            // Derive state from the surveys collection
            const lastSurveyShownAt: Date | null = pastSurveys.length > 0
                ? pastSurveys[0].submittedAt?.toDate() ?? null
                : null;
            const surveyedPlanIds = new Set(pastSurveys.map((d) => d.planId));

            // Enforce 90-day global cooldown
            if (lastSurveyShownAt && dayjs().diff(dayjs(lastSurveyShownAt), "day") < 90) {
                return;
            }

            const now = dayjs();
            const thirtyDaysAgo = now.subtract(30, "day");

            // Plans are already sorted by date desc from PlansService
            const planTriggeringSurvey = plans.find((plan) => {
                const planDate = dayjs(plan.date);
                return (
                    planDate.isBefore(now) &&
                    planDate.isAfter(thirtyDaysAgo) &&
                    !surveyedPlanIds.has(plan.id)
                );
            });

            if (planTriggeringSurvey) {
                this._planTriggeringSurvey = planTriggeringSurvey;
                this._shouldShowSurvey = true;
            }
        } catch (error) {
            console.error("SurveyService: error checking plans", error);
        }
    }

    async markSurveyShown(userId: string, planId: string): Promise<void> {
        this._shouldShowSurvey = false;
        try {
            await addDoc(collection(this._firebaseService.db, "surveys"), {
                userId,
                planId,
                type: "planCheckIn",
                dismissed: true,
                submittedAt: Timestamp.now(),
            });
        } catch (error) {
            console.error("SurveyService: error marking survey shown", error);
        }
    }

    async submitFeedback(feedback: SurveyFeedback): Promise<void> {
        await addDoc(collection(this._firebaseService.db, "surveys"), {
            ...feedback,
            type: "planCheckIn",
            dismissed: false,
            planDate: Timestamp.fromDate(feedback.planDate),
            submittedAt: Timestamp.fromDate(feedback.submittedAt),
        });
        this._shouldShowSurvey = false;
    }
}
